import React, { useState } from 'react'
import {
	Typography,
	Row,
	Col,
	Switch,
	Button,
	Modal,
	Table,
	Space,
	DatePicker,
	Checkbox,
	Tag,
	message,
} from 'antd'
import { UserOutlined, ReloadOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import { httpGet } from '../../common/http.service'

const { Title, Text, Paragraph } = Typography

interface SingleDeviceLoginRecord {
	id: string
	dateKey?: string | null
	staffId: string
	staffName: string | null
	previousDeviceId: string | null
	previousDeviceName: string | null
	deviceId: string | null
	deviceName: string | null
	authTime: number | null
	updatedAt: string | null
}

interface SingleDeviceLoginSubTabProps {
	singleDeviceLogin: boolean
	onToggle: (value: boolean) => void
	saving: boolean
}

const SingleDeviceLoginSubTab: React.FC<SingleDeviceLoginSubTabProps> = ({
	singleDeviceLogin,
	onToggle,
	saving,
}) => {
	const [recordOpen, setRecordOpen] = useState(false)
	const [recordLoading, setRecordLoading] = useState(false)
	const [records, setRecords] = useState<SingleDeviceLoginRecord[]>([])
	const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs())
	const [onlyChanged, setOnlyChanged] = useState<boolean>(true)

	const loadRecords = async (
		dateVal: Dayjs | null = selectedDate,
		onlyChangedVal: boolean = onlyChanged,
	) => {
		try {
			setRecordLoading(true)
			const params: Record<string, any> = {
				limit: 300,
				onlyChanged: onlyChangedVal,
			}
			if (dateVal) {
				params.date = dateVal.format('YYYY-MM-DD')
			}
			const response: any = await httpGet(
				'/api/v1/admin/fraud-detection/single-device-login-records',
				params,
			)
			setRecords(Array.isArray(response?.data) ? response.data : [])
		} catch (error) {
			console.error('Failed to load single-device login records:', error)
			message.error('Failed to load single-device login records')
		} finally {
			setRecordLoading(false)
		}
	}

	const columns = [
		{
			title: 'Date',
			key: 'dateKey',
			width: 115,
			render: (_: any, r: SingleDeviceLoginRecord) =>
				r.dateKey || (r.updatedAt ? dayjs(r.updatedAt).format('YYYY-MM-DD') : '—'),
		},
		{
			title: 'Staff ID',
			dataIndex: 'staffId',
			width: 110,
		},
		{
			title: 'Staff Name',
			dataIndex: 'staffName',
			width: 180,
			render: (v: string | null) => v || '—',
		},
		{
			title: 'Current Device',
			key: 'device',
			render: (_: any, r: SingleDeviceLoginRecord) =>
				r.deviceId ? `${r.deviceName || 'Unknown'} (${r.deviceId})` : '—',
		},
		{
			title: 'Previous Device',
			key: 'previousDevice',
			render: (_: any, r: SingleDeviceLoginRecord) =>
				r.previousDeviceId ? (
					<Tag color='volcano'>
						{`${r.previousDeviceName || 'Unknown'} (${r.previousDeviceId})`}
					</Tag>
				) : (
					<Tag color='green'>No previous device</Tag>
				),
		},
		{
			title: 'Auth Time',
			dataIndex: 'authTime',
			width: 170,
			render: (v: number | null) => (v ? dayjs.unix(v).format('YYYY-MM-DD HH:mm:ss') : '—'),
		},
		{
			title: 'Switched / Updated At',
			dataIndex: 'updatedAt',
			width: 170,
			render: (v: string | null) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm:ss') : '—'),
		},
	]

	return (
		<div>
			<Title level={4} style={{ marginBottom: 16 }}>
				<UserOutlined style={{ marginRight: 8 }} />
				Single Device Login Settings
			</Title>
			<Paragraph type='secondary' style={{ marginBottom: 24 }}>
				Configure rules for staff account access across multiple devices.
			</Paragraph>

			<Row align='middle' justify='space-between' style={{ padding: '12px 0' }}>
				<Col flex='1'>
					<Title level={5} style={{ margin: 0 }}>
						Single Device Login
					</Title>
					<Text type='secondary'>
						When enabled, each staff account can be active on only one device. If a
						newer login happens on another device, the previous device session will be
						rejected on next API call.
					</Text>
					<div style={{ marginTop: 12 }}>
						<Button
							icon={<UserOutlined />}
							onClick={async () => {
								setRecordOpen(true)
								await loadRecords(selectedDate, onlyChanged)
							}}
						>
							View device login change record
						</Button>
					</div>
				</Col>
				<Col>
					<Switch
						checked={singleDeviceLogin}
						onChange={onToggle}
						loading={saving}
						checkedChildren='Enabled'
						unCheckedChildren='Disabled'
					/>
				</Col>
			</Row>

			<Modal
				title={
					<Space wrap>
						<UserOutlined />
						<span>Single Device Login Records</span>
						<DatePicker
							size='small'
							allowClear
							placeholder='All Days'
							value={selectedDate}
							onChange={async (date) => {
								setSelectedDate(date)
								await loadRecords(date, onlyChanged)
							}}
						/>
						<Button
							size='small'
							onClick={async () => {
								setSelectedDate(null)
								await loadRecords(null, onlyChanged)
							}}
						>
							All Days
						</Button>
						<Checkbox
							checked={onlyChanged}
							onChange={async (e) => {
								const next = e.target.checked
								setOnlyChanged(next)
								await loadRecords(selectedDate, next)
							}}
						>
							Only Device Changes
						</Checkbox>
						<Button
							size='small'
							icon={<ReloadOutlined />}
							loading={recordLoading}
							onClick={() => loadRecords(selectedDate, onlyChanged)}
						>
							Refresh
						</Button>
					</Space>
				}
				open={recordOpen}
				onCancel={() => setRecordOpen(false)}
				footer={null}
				width={1100}
			>
				<Table<SingleDeviceLoginRecord>
					rowKey={(r) => r.id}
					loading={recordLoading}
					dataSource={records}
					scroll={{ x: 'max-content' }}
					pagination={{
						pageSize: 10,
						showSizeChanger: true,
						showTotal: (total) => `Total ${total} records`,
					}}
					columns={columns}
				/>
			</Modal>
		</div>
	)
}

export default SingleDeviceLoginSubTab
